import { type JsonValue, Value } from "@bufbuild/protobuf";
import { invoke } from "@tauri-apps/api/core";
import { produce } from "immer";
import isEmpty from "lodash-es/isEmpty";
import {
	type ActorRefFrom,
	type ActorRefFromLogic,
	type EventObject,
	and,
	assign,
	emit,
	enqueueActions,
	fromCallback,
	fromPromise,
	not,
	or,
	raise,
	sendParent,
	setup,
} from "xstate";

import { metadataStoreId } from "@moment/api-collab";
import {
	type BaseSpec,
	type DocumentMetadata,
	type PageMetadata,
	type PublishSettings,
} from "@moment/api-collab/api-types";
import { type QueryPersistence, Store } from "@moment/api-collab/differential";
import {
	RebootContext,
	type RebootInstance,
	RebootStore,
	RebootSubscriber,
	getBaseRebootUrl,
} from "@moment/app/data/reboot";
import { Logger } from "@moment/logging";
import { isRunningInIframe } from "@moment/ui";

import { DocsApiError, docsApi } from "~/state/api/docsapi";

import { isDesktopAppMode } from "../../../auth/interop/common";
import { LOCAL_WORKSPACE_NAME } from "../../../strings";
import { filesystemApi } from "../../api/filesystem";
import { type DocumentListItem } from "../../types/document.types";
import {
	type AuthenticatedContext,
	type AuthenticationMachine,
	authenticationActorRef,
} from "../authentication";
import {
	RebootQueryPersistence,
	queryEvaluatorStateFromReboot,
} from "../base/RebootQueryPersistence";
import { baseMachine } from "../base/base.machine";
import { gitMergeMachine } from "../git/merging/git-merge.machine";
import { gitRepositoryMachine } from "../git/repository/git-repository.machine";
import { createPageMachine } from "../pages/create-page.machine";
import { pageMachine } from "../pages/page.machine";
import { sharingMachine } from "../permissions/sharing.machine";
import { transferMachine } from "../permissions/transfer.machine";
import { syncMachine } from "../sync/sync.machine";
import { cloneDocumentMachine } from "./clone-document.machine";
import { deleteDocumentMachine } from "./delete-document.machine";

const logger = new Logger("document.machine");

// const debuglog = (_msg: string, ...args: any[]) => {
// 	console.debug(`[document machine] ${_msg}`, ...args);
// };
const debuglog = (_msg: string, ..._args: unknown[]) => {};

const loadDocumentMetadataFromDisk = fromPromise<
	DocumentMetadata | undefined,
	{ documentPath: string }
>(({ input }) => filesystemApi.document.metadata.read(input.documentPath));

const loadDocumentMetadataFromRemote = fromPromise<
	DocumentMetadata,
	{ workspaceName: string; documentId: string }
>(async ({ input }) => docsApi.document.metadata.read(input.workspaceName, input.documentId));

const persistDocumentMetadata = fromPromise<
	void,
	{ documentPath: string; metadata: DocumentMetadata }
>(({ input }) => filesystemApi.document.metadata.write(input.documentPath, input.metadata));

export function workspaceNameFromDocumentListItem(listItem: DocumentListItem) {
	return listItem.workspaces[0]?.name ?? LOCAL_WORKSPACE_NAME;
}

export function remoteWorkspaceNameFromDocumentListItem(listItem: DocumentListItem) {
	return (
		listItem.workspaces.find((workspace) => workspace.name !== LOCAL_WORKSPACE_NAME)?.name ?? ""
	);
}

type DocumentMachineContext = {
	listItem: DocumentListItem;
	metadata?: DocumentMetadata;
	collabEnabled: boolean;
	reboot?: RebootInstance;
	store?: Store<{ metadata: DocumentMetadata }>;
	authenticationActorRef: ActorRefFrom<AuthenticationMachine>;
	baseActorRef?: ActorRefFrom<typeof baseMachine>;
	pageActorRefs: Record<string, ActorRefFrom<typeof pageMachine>>;
	gitRepositoryActorRef?: ActorRefFrom<typeof gitRepositoryMachine>;
	gitMergeActorRef?: ActorRefFrom<typeof gitMergeMachine>;
	remoteSyncActorRef?: ActorRefFrom<typeof syncMachine>;
	sharingActorRef?: ActorRefFrom<typeof sharingMachine>;
	transferActorRef?: ActorRefFrom<typeof transferMachine>;
	error?: Error;
	baseActorIsWarm: boolean;
	gitActorIsWarm: boolean;
	ssr: boolean;
};

type DocumentMachineInput = {
	listItem: DocumentListItem;
	authenticationActorRef: ActorRefFrom<AuthenticationMachine>;
};

type DocumentMachineEvent =
	| { type: "document.opened"; payload?: { ssr: boolean } }
	| { type: "document.collab.enabled" }
	| { type: "document.collab.disabled" }
	| { type: "document.clone" }
	| { type: "document.deleted" }
	| { type: "document.closed" }
	| { type: "document.listItemChanged"; payload: { listItem: DocumentListItem } }
	| { type: "document.nameChanged"; payload: { newName: string } }
	| {
			type: "document.publishSettingsChanged";
			payload: { publishSettings: PublishSettings };
	  }
	| {
			type: "document.baseSpecChanged";
			payload: { base: BaseSpec };
	  }
	| {
			type: "pages.reordered";
			payload: { reorderInfo: { pageId: string; parentPageID: string | null }[] };
	  }
	| { type: "page.nameChanged"; payload: { pageId: string; newName: string } }
	| { type: "page.changesPersisted"; payload: { pageId: string; documentId: string } }
	| { type: "page.added"; payload?: { pageMetadata: PageMetadata } }
	| { type: "page.archived"; payload: { pageId: string } }
	| { type: "page.unarchived"; payload: { pageId: string } }
	| { type: "page.persisted" }
	| { type: "git.branchSwitched" }
	| { type: "base.ready" }
	| { type: "base.error"; payload?: { error: Error } }
	| { type: "git.repository.ready" }
	| { type: "document.error" }
	| {
			type: "document.metadata.changed";
			payload: { source: "local" | "remote"; metadata: DocumentMetadata };
	  }
	| {
			type: "document.collab.connected";
			payload: { store: Store<{ metadata: DocumentMetadata }> };
	  }
	| { type: "document.loggedIn" }
	| { type: "document.loggedOut" };

type DocumentMachineEmitted = { type: "page.added"; payload: { pageMetadata: PageMetadata } };

const connectToReboot = fromPromise(
	async ({
		input,
	}: {
		input: {
			workspaceName: string;
			documentId: string;
			metadata: DocumentMetadata;
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		};
	}): Promise<RebootInstance> => {
		const getBearerToken = async () => {
			const authCtx = authenticationActorRef.getSnapshot().context;
			return authCtx.authnStatus === "authenticated"
				? authCtx.getAccessTokenSilently()
				: undefined;
		};
		const context = new RebootContext({ url: getBaseRebootUrl(), bearerToken: getBearerToken });
		const store = RebootStore.ref(
			metadataStoreId(input.workspaceName, input.documentId, "main")
		);

		store.ensureCreated(context, {
			tables: {
				metadata: {
					permissions: [],
					initialTableData: [
						{
							id: "metadata",
							value: Value.fromJson(input.metadata as unknown as JsonValue),
						},
					],
				},
			},
		});

		return {
			context,
			store,
		};
	}
);

const makeRebootPersistence = async ({
	abortController,
	reboot,
	baseSpec,
}: {
	abortController: AbortController;
	reboot: RebootInstance;
	baseSpec: BaseSpec;
}): Promise<QueryPersistence> => {
	try {
		const { context, store } = reboot;

		const initialState = await queryEvaluatorStateFromReboot(
			context,
			store,
			baseSpec,
			abortController.signal
		);
		const queryPersistence = new RebootQueryPersistence(context, store, initialState);
		logger.info(`reboot connection initialized`, { url: context.url });
		return queryPersistence;
	} catch (error) {
		logger.error("Error creating query persistence", { error });
		if (abortController.signal.aborted) {
			logger.error("Base data fetch timed out");
			throw new Error("Base data fetch timed out");
		}
		const message = error instanceof Error ? error.message : "Unknown error";
		throw new Error(`Failed to create query persistence from Reboot store: ${message}`);
	}
};

const baseSpec: BaseSpec = {
	tables: {
		metadata: {
			permissions: [],
			columns: [
				{
					name: "metadata",
					type: "BLOB",
				},
			],
		},
	},
	views: {},
};

export const documentMachine = setup({
	types: {
		context: {} as DocumentMachineContext,
		input: {} as DocumentMachineInput,
		events: {} as DocumentMachineEvent,
		emitted: {} as DocumentMachineEmitted,
	},
	actors: {
		loadDocumentMetadataFromDisk,
		loadDocumentMetadataFromRemote,
		cloneDocument: cloneDocumentMachine,
		persistDocumentMetadata,
		deleteDocument: deleteDocumentMachine,
		createPage: createPageMachine,
		connectToReboot,
		listenForChanges: fromCallback<
			EventObject,
			{
				reboot: RebootInstance;
			}
		>(({ input, sendBack }) => {
			const abortController = new AbortController();

			async function run() {
				const queryPersistence = await makeRebootPersistence({
					abortController,
					reboot: input.reboot,
					baseSpec,
				});

				// TODO: permission check
				const store = new Store<{ metadata: DocumentMetadata }>(
					queryPersistence,
					{
						// baseSpec for this machine is hard-coded
						localBaseSpecReplica: () => {
							return baseSpec;
						},
						saveBaseSpec: async () => {},
					},
					true
				);

				sendBack({
					type: "document.collab.connected",
					payload: { store },
				});

				new RebootSubscriber(input.reboot, store, (error) =>
					sendBack({
						type: "document.collab.error",
						payload: {
							error,
						},
					})
				);

				store.from("metadata").subscribe((event: DocumentMetadata[]) => {
					sendBack({
						type: "document.metadata.changed",
						payload: {
							source: "remote",
							metadata: event[0],
						},
					});
				});
			}

			run();

			return () => abortController.abort();
		}),
		listenForLogin: fromCallback<
			EventObject,
			{ authenticationActorRef: ActorRefFrom<AuthenticationMachine> }
		>(({ sendBack, input }) => {
			input.authenticationActorRef.on("authn.succeeded", (event) => {
				debuglog("Detected login, fetching document", { event });
				sendBack({ type: "document.loggedIn" });
			});
			input.authenticationActorRef.on("authn.logout", (event) => {
				debuglog("Detected logout, clearing document", { event });
				sendBack({ type: "document.loggedOut" });
			});
		}),
	},
	guards: {
		fetchingRemoteErrorIsNotFound: ({ context }) => {
			if (context.error instanceof DocsApiError && context.error.cause instanceof Response) {
				return context.error.cause.status === 404;
			}
			return false;
		},
		isDesktopAppMode: () => isDesktopAppMode(),

		isCollabEnabled: ({ context }) => {
			return context.collabEnabled;
		},

		documentExistsOnDisk: ({ context }) => {
			return context.listItem.path !== undefined;
		},

		userIsAuthorizedToReadDocument: ({ context }) => {
			if (context.error === undefined) {
				return true;
			}

			const hasLocal = context.listItem?.workspaces.some(
				(workspace) => workspace.name === LOCAL_WORKSPACE_NAME
			);

			if (hasLocal) {
				// if user has a local workspace, they already have a copy and should return true.
				return true;
			}

			const failedToFetchMetadata =
				context.error?.message?.includes("'Failed to fetch document metadata from API'") ??
				false;

			const result = !failedToFetchMetadata;
			return result;
		},
		userIsAuthorizedToEditDocument: () => {
			// TODO: write permission check
			return true;
		},

		documentShouldBeRenamedOnPageNameChange: ({ context }) => {
			const documentHasOnePage = context.metadata?.pagesMetadata.length === 1;
			const documentHasNoTitle = isEmpty(context.listItem.title);

			return documentHasOnePage && documentHasNoTitle;
		},

		hasRemoteWorkspace: ({ context }) => {
			debuglog("checking if remote workspace", {
				workspace: context.listItem.workspaces,
			});
			for (const workspace of context.listItem.workspaces) {
				if (workspace.id === LOCAL_WORKSPACE_NAME) {
					continue;
				}
				return true;
			}
			return false;
		},

		hasMomentOriginRemote: ({ context }) => {
			return true;
		},

		// note: This does NOT get reevaluated when a login happens through the authentication actor.
		// app state has a actor called "listenForLogin" that listens for login events and the triggers
		// a workspaceList.refetch and documentList.refetch. that updates this.
		// if you want your state to update when a login happens, you need to register an event to do that.
		isUserAuthenticated: ({ context }) => {
			return (
				context.authenticationActorRef.getSnapshot().context.authnStatus === "authenticated"
			);
		},

		isSsr: ({ context }) => {
			return context.ssr;
		},

		gitActorIsWarm: ({ context }) => {
			return context.gitActorIsWarm;
		},

		baseActorIsWarm: ({ context }) => {
			return context.baseActorIsWarm;
		},
	},
	actions: {
		updateDocumentList: sendParent({
			type: "documentList.refetch",
		}),

		spawnBaseActor: enqueueActions(({ context, enqueue }) => {
			const { id: documentId, path } = context.listItem;

			if (!documentId) {
				return {};
			}

			// do not spawn a base actor if one exists
			if (context.baseActorRef) {
				return {};
			}

			const workspaceName = workspaceNameFromDocumentListItem(context.listItem);

			enqueue.assign(({ context, spawn }) => {
				const baseActorRef = spawn(baseMachine, {
					input: {
						workspaceName,
						documentId,
						documentPath: path ?? undefined,
						baseSpec: context.metadata?.base,
						authenticationActorRef: context.authenticationActorRef,
					},
					systemId: `base:${documentId}`,
				});

				baseActorRef.send({ type: "base.opened" });

				// register the base actor with the remote sync actor if it exists
				if (context.remoteSyncActorRef) {
					enqueue.sendTo(context.remoteSyncActorRef, {
						type: "sync.baseActorChanged",
						baseActorRef,
					});
				}

				return { baseActorRef };
			});
		}),

		stopBaseActor: enqueueActions(({ context, enqueue }) => {
			debuglog("stopping base actor", {
				workspace: workspaceNameFromDocumentListItem(context.listItem),
				documentId: context.listItem.id,
				baseMachine: context.baseActorRef,
			});
			if (context.baseActorRef) {
				enqueue.stopChild(context.baseActorRef);
			}

			enqueue.assign({ baseActorRef: undefined });
		}),

		spawnGitActor: assign(({ context, spawn }) => {
			const { id: documentId, path } = context.listItem;

			if (!documentId || !path) {
				return {};
			}

			// If this actor already exists, don't spawn a new one
			if (context.gitRepositoryActorRef) {
				return {};
			}

			const actor = spawn(gitRepositoryMachine, {
				input: {
					path,
				},
				systemId: `git-repository:${documentId}`,
			});

			if (context.remoteSyncActorRef) {
				// Register the git actor with the remote sync actor if it exists
				context.remoteSyncActorRef.send({
					type: "sync.gitActorChanged",
					gitRef: actor,
				});
			}

			return {
				gitRepositoryActorRef: actor,
			};
		}),

		stopGitActor: enqueueActions(({ context, enqueue }) => {
			if (context.gitRepositoryActorRef) {
				enqueue.stopChild(context.gitRepositoryActorRef);
			}

			enqueue.assign({
				gitRepositoryActorRef: undefined,
			});
		}),

		spawnGitMergeActor: enqueueActions(({ context, enqueue }) => {
			const { id: documentId } = context.listItem;

			if (!context.gitRepositoryActorRef) {
				return;
			}

			// If this actor already exists, stop and respawn it
			if (context.gitMergeActorRef) {
				enqueue.stopChild(context.gitMergeActorRef);
			}

			enqueue.assign(({ spawn }) => ({
				gitMergeActorRef: spawn(gitMergeMachine, {
					input: {
						gitRepositoryActorRef: context.gitRepositoryActorRef!,
						workspaceName: workspaceNameFromDocumentListItem(context.listItem)!,
						documentId,
					},
					systemId: `git-merge:${documentId}`,
				}),
			}));
		}),

		stopGitMergeActor: enqueueActions(({ context, enqueue }) => {
			if (context.gitMergeActorRef) {
				enqueue.stopChild(context.gitMergeActorRef);
			}

			enqueue.assign({
				gitMergeActorRef: undefined,
			});
		}),

		spawnSharingActor: assign(({ context, spawn }) => {
			const { id: documentId } = context.listItem;

			// If this actor already exists, don't spawn a new one
			if (context.sharingActorRef) {
				return {};
			}

			// do not spawn a sharing actor if the user is not authenticated
			if (
				context.authenticationActorRef.getSnapshot().context.authnStatus !== "authenticated"
			) {
				return {};
			}

			const authInfo = context.authenticationActorRef.getSnapshot()
				.context as AuthenticatedContext;

			const personalWorkspace = authInfo.user.nickname;

			const workspace =
				remoteWorkspaceNameFromDocumentListItem(context.listItem) ?? personalWorkspace;
			return {
				sharingActorRef: spawn(sharingMachine, {
					input: {
						workspace,
						documentId,
						authenticationActorRef: context.authenticationActorRef,
						gitRef: context.gitRepositoryActorRef!,
					},
					systemId: `sharing:${documentId}`,
				}),
			};
		}),

		spawnTransferActor: assign(({ context, spawn }) => {
			const { id: documentId } = context.listItem;

			const workspace = remoteWorkspaceNameFromDocumentListItem(context.listItem) ?? "";

			return {
				transferActorRef: spawn(transferMachine, {
					input: {
						workspace,
						documentId,
						authenticationActorRef: context.authenticationActorRef,
						gitRef: context.gitRepositoryActorRef!,
					},
					systemId: `transfer:${documentId}`,
				}),
			};
		}),

		stopSharingActor: enqueueActions(({ context, enqueue }) => {
			if (context.sharingActorRef) {
				enqueue.stopChild(context.sharingActorRef);
			}

			enqueue.assign({
				sharingActorRef: undefined,
			});
		}),

		spawnSyncActor: assign(({ context, spawn }) => {
			const { id: documentId, path: path } = context.listItem;

			// If this actor already exists, don't spawn a new one
			if (context.remoteSyncActorRef) {
				return {};
			}

			const workspaceName = remoteWorkspaceNameFromDocumentListItem(context.listItem);

			return {
				remoteSyncActorRef: spawn(syncMachine, {
					input: {
						workspaceName,
						documentId,
						documentPath: path || "",

						authenticationActorRef: context.authenticationActorRef!,
						gitRef: context.gitRepositoryActorRef!,
						baseActorRef: context.baseActorRef!,
					},
					systemId: `sync:${documentId}`,
				}),
			};
		}),

		stopSyncActor: enqueueActions(({ context, enqueue }) => {
			if (context.remoteSyncActorRef) {
				enqueue.stopChild(context.remoteSyncActorRef);
			}

			enqueue.assign({
				remoteSyncActorRef: undefined,
			});
		}),

		spawnPageActors: enqueueActions(({ context, enqueue }) => {
			const pageActorRefs = context.pageActorRefs;

			enqueue.assign(({ spawn }) => {
				context.metadata?.pagesMetadata.forEach((pageMetadata) => {
					// Check if the pageActorRef already exists, if it does, just send a refresh message
					const existingPageActorRef = pageActorRefs[pageMetadata.id];
					if (existingPageActorRef) {
						enqueue.sendTo(existingPageActorRef, {
							type: "page.refresh",
						});
						return;
					}

					const pageActorRef = spawn(pageMachine, {
						systemId: `page:${context.listItem.id}:${pageMetadata.id}`,
						input: {
							workspaceName: workspaceNameFromDocumentListItem(context.listItem),
							documentId: context.listItem.id,
							pageId: pageMetadata.id,
							documentPath: context.listItem.path ?? undefined,
						},
					});
					pageActorRefs[pageMetadata.id] = pageActorRef;
				});

				return { pageActorRefs };
			});
		}),

		updateGitStatus: enqueueActions(({ context, enqueue }) => {
			if (context.gitRepositoryActorRef) {
				enqueue.sendTo(context.gitRepositoryActorRef, {
					type: "git.status",
				});
			}

			if (context.remoteSyncActorRef) {
				enqueue.sendTo(context.remoteSyncActorRef, {
					type: "sync.regeneratePlan",
				});
			}
		}),

		updateRemoteSyncStatus: enqueueActions(({ context, enqueue }) => {
			if (!context.remoteSyncActorRef) {
				return;
			}

			const workspace = workspaceNameFromDocumentListItem(context.listItem);

			// no need to update remote sync status for local workspaces
			if (workspace === LOCAL_WORKSPACE_NAME) {
				return;
			}

			enqueue.sendTo(context.remoteSyncActorRef, {
				type: "sync.regeneratePlan",
			});
		}),

		updateWorkspace: enqueueActions(({ context, enqueue, event }) => {
			if (event.type !== "document.listItemChanged") {
				return;
			}

			const workspaceName = workspaceNameFromDocumentListItem(event.payload.listItem);
			if (workspaceName === LOCAL_WORKSPACE_NAME) {
				return;
			}
			if (context.sharingActorRef) {
				enqueue.sendTo(context.sharingActorRef, {
					type: "sharing.attachWorkspace",
					workspace: workspaceName,
				});
			}

			if (context.remoteSyncActorRef) {
				enqueue.sendTo(context.remoteSyncActorRef, {
					type: "sync.resyncWorkspace",
					workspace: workspaceName,
				});
			}

			if (context.baseActorRef) {
				enqueue.sendTo(context.baseActorRef, {
					type: "base.updateWorkspaceName",
					workspaceName,
				});
			}
		}),

		renameDocument: enqueueActions(({ context, enqueue, event }) => {
			if (event.type !== "document.nameChanged" && event.type !== "page.nameChanged") {
				return;
			}

			const { newName: title } = event.payload;

			const listItem = { ...context.listItem, title };
			const metadata = { ...context.metadata!, title };

			enqueue.assign({
				listItem,
				metadata,
			});

			enqueue.sendParent({
				type: "documentList.updated",
				payload: { listItems: [listItem] },
			});

			enqueue(
				() =>
					void invoke("update_recent_docs", {
						removeItem: false,
						recentDoc: {
							title: context.listItem.title,
							workspaceName:
								workspaceNameFromDocumentListItem(context.listItem) ??
								LOCAL_WORKSPACE_NAME,
							documentId: context.listItem.id,
						},
					})
			);
		}),

		renamePage: assign(({ context, event }) => {
			if (event.type !== "page.nameChanged") {
				return {};
			}

			return produce(context, (draft) => {
				const pageMetadata = draft.metadata?.pagesMetadata.find(
					(pageMetadata) => pageMetadata.id === event.payload.pageId
				);
				if (pageMetadata) {
					pageMetadata.title = event.payload.newName;
				}
			});
		}),

		updateDocumentBaseSpec: assign(({ context, event }) => {
			if (event.type !== "document.baseSpecChanged") {
				return {};
			}

			return produce(context, (draft) => {
				if (draft.metadata) {
					draft.metadata.base = event.payload.base;
				}
			});
		}),
	},
}).createMachine({
	id: "document",
	type: "parallel",
	context: ({ input }) => ({
		collabEnabled: false,
		listItem: input.listItem,
		authenticationActorRef: input.authenticationActorRef,
		pageActorRefs: {},
		baseActorIsWarm: false,
		gitActorIsWarm: false,
		ssr: false,
	}),
	states: {
		data: {
			initial: "idle",
			states: {
				idle: {
					on: {
						"document.opened": {
							guard: "userIsAuthorizedToReadDocument",
							target: "fetching",
							actions: [
								assign(({ event }) => ({
									ssr: event.payload?.ssr === true,
								})),
								(event) =>
									debuglog("opening document", {
										isclient: typeof window !== "undefined",
										isRunningInIframe: isRunningInIframe(),
										event,
									}),
							],
						},
					},
				},
				fetching: {
					description: "Fetching the document metadata",
					initial: "idle",
					states: {
						idle: {
							always: [
								{
									guard: "documentExistsOnDisk",
									target: "fetchingFromDisk",
								},
								{
									guard: and([
										"hasRemoteWorkspace",
										"userIsAuthorizedToReadDocument",
									]),
									target: "fetchingFromRemote",
								},
								{
									guard: not(
										and([
											"hasRemoteWorkspace",
											"userIsAuthorizedToReadDocument",
										])
									),
									actions: [sendParent({ type: "document.error" })],
									target: "error",
								},
							],
						},
						fetchingFromDisk: {
							invoke: {
								src: "loadDocumentMetadataFromDisk",
								input: ({ context }) => ({
									documentPath: context.listItem.path!,
								}),
								onDone: {
									actions: [
										assign({
											metadata: ({ event }) => event.output,
										}),
										({ event }) => debuglog("fetchingFromDisk.onDone", event),
									],
									target: "spawningChildren",
								},
								onError: {
									actions: [
										({ event }) => {
											logger.error(
												"Error loading document metadata from disk",
												{
													cause: event.error,
												}
											);
										},
										assign({
											error: ({ event }) => event.error as Error,
										}),
									],
									target: "#document.data.error",
								},
							},
						},
						fetchingFromRemote: {
							guard: and(["hasRemoteWorkspace", "isUserAuthenticated"]),
							entry: ({ context }) => {
								debuglog("entering fetchingFromRemote", {
									workspaceName: workspaceNameFromDocumentListItem(
										context.listItem
									),
									documentId: context.listItem.id,
								});
							},
							invoke: {
								src: "loadDocumentMetadataFromRemote",
								input: ({ context }) => ({
									workspaceName: workspaceNameFromDocumentListItem(
										context.listItem
									)!,
									documentId: context.listItem.id,
								}),
								onDone: {
									actions: [
										assign({
											metadata: ({ event }) => event.output,
										}),
										({ event }) => debuglog("fetchingFromRemote.onDone", event),
									],
									target: "spawningChildren",
								},
								onError: {
									actions: [
										({ event }) =>
											logger.error(
												"Error loading document metadata from remote",
												{
													cause: event.error,
												}
											),
										assign({
											error: ({ event }) => event.error as Error,
										}),
									],
									target: "error",
								},
							},
						},
						spawningChildren: {
							initial: "spawningPages",
							states: {
								spawningPages: {
									entry: [
										() => debuglog("Spawning pageActors"),
										"spawnPageActors",
									],
									always: [
										// If we're in SSR, we can just skip the other dependencies and be finished
										{
											guard: "isSsr",
											target: "done",
											actions: () =>
												debuglog("Skipping spawning children in SSR"),
										},
										{ target: "spawningDependencies" },
									],
								},
								spawningDependencies: {
									entry: [
										() => debuglog("Spawning dependencies"),
										"spawnGitActor",
										"spawnBaseActor",
									],
									always: [
										{
											guard: and([
												"userIsAuthorizedToEditDocument",
												// We should check to see if the gitActor is warm but it is not currently firing the ready event
												// "gitActorIsWarm",
												"baseActorIsWarm",
											]),
											target: "spawningChildrenWithDependencies",
										},
									],
								},
								spawningChildrenWithDependencies: {
									entry: [
										() => debuglog("Spawning children with dependencies"),
										"spawnGitMergeActor",
										"spawnSyncActor",
										"spawnSharingActor",
										"spawnTransferActor",
									],
									always: {
										target: "done",
									},
								},
								done: {
									type: "final",
								},
							},
							onDone: {
								target: "done",
							},
						},
						done: {
							entry: () => debuglog("fetching.done"),
							type: "final",
						},
						error: {
							entry: ({ context, event }) => {
								debuglog("Error fetching document metadata", {
									error: context.error,
									event,
									context,
								});
							},
							always: [
								{
									guard: "fetchingRemoteErrorIsNotFound",
									target: "fetchingFromDisk",
								},
								{
									guard: and([
										"documentExistsOnDisk",
										// i am not sure why this happens.
										// but i did see metadata but nullish while documentExistsOnDisk is true
										({ context }) => {
											return context.metadata === undefined;
										},
									]),
									target: "fetchingFromDisk",
								},
								{
									guard: not("userIsAuthorizedToReadDocument"),
									target: "done",
								},
							],
						},
					},
					onDone: [
						{
							guard: not("userIsAuthorizedToReadDocument"),
							actions: () => debuglog("not authorized, transitioning to error"),
							target: "error",
						},
						// If the document doesn't exist on disk, we need to clone it
						{
							guard: or([not("isDesktopAppMode"), not("documentExistsOnDisk")]),
							actions: () => debuglog("transitioning to viewing"),
							target: "viewing",
						},

						// If the user is authorized to edit the document, we can automatically
						// transition to the editing state
						{
							guard: "userIsAuthorizedToEditDocument",
							actions: () => debuglog("transitioning to editing"),
							target: "editing",
						},
					],
				},

				viewing: {
					entry: () => debuglog("viewing document"),
					on: {
						"document.clone": {
							target: "cloning",
						},
					},
				},

				cloning: {
					invoke: {
						src: "cloneDocument",
						input: ({ context }) => ({
							workspaceName: workspaceNameFromDocumentListItem(context.listItem)!,
							documentId: context.listItem.id,
						}),
						onDone: {
							actions: assign(({ context, event }) => ({
								listItem: {
									...context.listItem,
									path: event.output.path,
								},
							})),
							target: "editing",
						},
						onError: {
							target: "error",
						},
					},
					// after cloning, we need to update the document list and restart the git machine
					exit: [
						"updateDocumentList",
						"stopGitActor",
						"spawnGitActor",
						"stopGitMergeActor",
						"spawnGitMergeActor",
						"stopBaseActor",
						"spawnBaseActor",
					],
				},

				editing: {
					description: "Editing the document in the app",
					type: "parallel",
					entry: ({ context }) =>
						void invoke("update_recent_docs", {
							removeItem: false,
							recentDoc: {
								title: context.listItem.title,
								workspaceName:
									workspaceNameFromDocumentListItem(context.listItem) ??
									LOCAL_WORKSPACE_NAME,
								documentId: context.listItem.id,
							},
						}),
					states: {
						persistence: {
							initial: "idle",
							states: {
								idle: {},
								requesting: {
									entry: raise(({ context }) => ({
										type: "document.metadata.changed",
										payload: {
											source: "local",
											metadata: context.metadata!,
										},
									})),
									always: {
										target: "debouncing",
									},
								},
								debouncing: {
									after: {
										1000: "persisting",
									},
								},
								persisting: {
									invoke: {
										src: "persistDocumentMetadata",
										input: ({ context }) => ({
											documentPath: context.listItem.path!,
											metadata: context.metadata!,
										}),
										onDone: {
											target: "idle",
										},
										onError: {
											actions: ({ event }) => {
												logger.error("Error persisting document metadata", {
													cause: event.error,
												});
											},
											target: "#document.data.error",
										},
									},
									exit: ["updateGitStatus", "updateRemoteSyncStatus"],
								},
								addingPage: {
									invoke: {
										src: "createPage",
										input: ({ context }) => ({
											workspaceName: workspaceNameFromDocumentListItem(
												context.listItem
											)!,
											documentId: context.listItem.id,
											documentPath: context.listItem.path!,
										}),
										onDone: {
											actions: [
												assign(({ context, event, spawn }) => ({
													metadata: {
														...context.metadata!,
														pagesMetadata: [
															...context.metadata!.pagesMetadata,
															event.output.pageMetadata,
														],
													},
													pageActorRefs: {
														...context.pageActorRefs,
														[event.output.pageMetadata.id]: spawn(
															pageMachine,
															{
																systemId: `page:${context.listItem.id}:${event.output.pageMetadata.id}`,
																input: {
																	workspaceName:
																		workspaceNameFromDocumentListItem(
																			context.listItem
																		),
																	documentId: context.listItem.id,
																	pageId: event.output
																		.pageMetadata.id,
																	documentPath:
																		context.listItem.path!,
																},
															}
														),
													},
												})),
												emit(({ event }) => ({
													type: "page.added",
													payload: {
														pageMetadata: event.output.pageMetadata,
													},
												})),
											],
											target: "#document.data.editing.persistence.requesting",
										},
										onError: {
											actions: ({ event }) => {
												logger.error("Error creating page", {
													cause: event.error,
												});
											},
											target: "#document.data.editing.persistence.idle",
										},
									},
								},
							},
						},
					},
					on: {
						"document.publishSettingsChanged": {
							actions: assign(({ context, event }) => ({
								metadata: {
									...context.metadata!,
									publishSettings: event.payload.publishSettings,
								},
							})),
							target: "#document.data.editing.persistence.requesting",
						},
						"page.added": {
							target: "#document.data.editing.persistence.addingPage",
						},
						"page.nameChanged": [
							{
								guard: "documentShouldBeRenamedOnPageNameChange",
								actions: ["renamePage", "renameDocument"],
								target: "#document.data.editing.persistence.requesting",
							},
							{
								actions: "renamePage",
								target: "#document.data.editing.persistence.requesting",
							},
						],
						"document.baseSpecChanged": {
							actions: "updateDocumentBaseSpec",
							target: "#document.data.editing.persistence.requesting",
						},
						"pages.reordered": {
							actions: assign(({ context, event }) =>
								produce(context, (draft) => {
									const sortedPages = event.payload.reorderInfo.reduce<
										DocumentMetadata["pagesMetadata"]
									>((acc, { pageId, parentPageID }, index) => {
										const page = draft.metadata?.pagesMetadata.find(
											(page) => page.id === pageId
										);
										if (page) {
											const newPage = { ...page, parentPageID };
											if (index === 0) {
												newPage.slug = "index";
											} else if (page.slug === "index") {
												newPage.slug = page.id;
											}
											acc.push(newPage);
										}
										return acc;
									}, []);

									draft.metadata!.pagesMetadata = sortedPages;
								})
							),
							target: "#document.data.editing.persistence.requesting",
						},
						"page.archived": {
							actions: assign(({ context, event }) =>
								produce(context, (draft) => {
									if (!draft.metadata) {
										return;
									}

									const pageMetadata = draft.metadata.pagesMetadata.find(
										(page) => page.id === event.payload.pageId
									);
									if (!pageMetadata) {
										return;
									}

									pageMetadata.archived = true;
								})
							),
							target: "#document.data.editing.persistence.requesting",
						},
						"page.unarchived": {
							actions: assign(({ context, event }) =>
								produce(context, (draft) => {
									if (!draft.metadata) {
										return;
									}

									const pageMetadata = draft.metadata.pagesMetadata.find(
										(page) => page.id === event.payload.pageId
									);
									if (!pageMetadata) {
										return;
									}

									pageMetadata.archived = false;
								})
							),
							target: "#document.data.editing.persistence.requesting",
						},
						"document.nameChanged": {
							actions: "renameDocument",
							target: "#document.data.editing.persistence.requesting",
						},
						"document.deleted": {
							target: "deleting",
						},
					},
				},

				deleting: {
					invoke: {
						src: "deleteDocument",
						input: ({ context }) => ({
							documentId: context.listItem.id,
							workspaces: context.listItem.workspaces,
						}),
						onError: {
							actions: [
								({ event }) => {
									logger.error("Error deleting document", { cause: event.error });
								},
								({ event }) => {
									throw event.error;
								},
							],
						},
						onDone: {
							actions: [
								({ context }) =>
									void invoke("update_recent_docs", {
										removeItem: true,
										recentDoc: {
											title: context.listItem.title,
											workspaceName:
												workspaceNameFromDocumentListItem(
													context.listItem
												) ?? "local",
											documentId: context.listItem.id,
										},
									}),
								sendParent(({ context }) => ({
									type: "document.deleted",
									payload: {
										documentId: context.listItem.id,
									},
								})),
							],
						},
					},
				},

				// wait for a login change and see what else we can do.
				error: {
					entry: (event) => console.error("document.machine error", event),
					invoke: {
						src: "listenForLogin",
						input: ({ context }) => ({
							authenticationActorRef: context.authenticationActorRef,
						}),
					},
				},
			},

			on: {
				"page.persisted": {
					actions: "updateGitStatus",
				},
				"document.loggedOut": {
					description: "Handle a user logging out",
					actions: [
						// we are going to refetch
						// so we should stop all the actors
						"stopSyncActor",
						"stopGitActor",
						"stopSharingActor",
						"stopGitMergeActor",
						"stopBaseActor",
					],
					target: "#document.data.fetching",
				},
				"document.loggedIn": {
					description: "Handle a user logging in",
					target: "#document.data.fetching",
					actions: [
						assign({
							error: undefined, // Clear any previous errors
						}),
						// we are going to refetch
						// so we should stop all the actors
						"stopSyncActor",
						"stopGitActor",
						"stopSharingActor",
						"stopGitMergeActor",
						"stopBaseActor",
					],
				},
				"page.changesPersisted": {
					actions: ["updateGitStatus", "updateRemoteSyncStatus"],
				},
				"git.branchSwitched": {
					actions: "updateRemoteSyncStatus",
					target: "#document.data.fetching",
				},
				"document.listItemChanged": {
					actions: [
						assign(({ event }) => ({
							listItem: event.payload.listItem,
						})),
						"updateWorkspace",
						"updateGitStatus",
					],
				},
				"document.remoteCreated.addWorkspace": {
					actions: [
						assign(({ event, context }) => ({
							listItem: {
								...context.listItem,
								workspaces: [...context.listItem.workspaces, event.workspace],
							},
						})),
					],
				},
				"document.error": {
					target: "#document.data.error",
				},
				"base.ready": {
					actions: [
						() => debuglog("base.ready event received"),
						assign({
							baseActorIsWarm: true,
						}),
					],
				},
				"base.error": {
					actions: [
						({ event }) => {
							logger.error("Base actor error", { cause: event.payload?.error });
						},
						assign({
							baseActorIsWarm: true,
							error: ({ event }) => event.payload?.error,
						}),
					],
				},
				"git.repository.ready": {
					actions: [
						() => debuglog("git.repository.ready event received"),
						assign({
							gitActorIsWarm: true,
						}),
					],
				},
				"document.closed": {
					description: "Close the document and clear its data out of the actor",
					target: "#document.data.idle",
					actions: [
						"stopSyncActor", // TODO: race conditions?
						"stopGitActor",
						"stopSharingActor",
						"stopGitMergeActor",
						"stopBaseActor",
					],
				},
			},
		},

		collab: {
			initial: "disabled",
			on: {
				"document.collab.disabled": {
					actions: [
						() => debuglog("collab disabled"),
						enqueueActions(({ context, enqueue }) => {
							Object.values(context.pageActorRefs).forEach((pageActorRef) => {
								enqueue.sendTo(pageActorRef, {
									type: "document.collab.disabled",
								});
							});
						}),
					],
					target: "collab.disabled",
				},
				"document.metadata.changed": {
					actions: [
						({ event }) => {
							debuglog("document.metadata.changed", event);
						},
						enqueueActions(({ context, event, enqueue }) => {
							switch (event.payload.source) {
								case "local":
									if (context.store) {
										context.store.delete("metadata", "metadata");
										context.store.insert(
											"metadata",
											"metadata",
											event.payload.metadata
										);
									}
									return;

								case "remote":
									if (!event.payload.metadata) {
										// Sometimes the payload comes through undefined and we don't want to assign the store version to undefined
										return;
									}
									enqueue.assign({
										metadata: event.payload.metadata,
									});
									return;
							}
						}),
					],
				},
			},
			states: {
				disabled: {
					on: {
						"document.collab.enabled": {
							actions: [
								() => debuglog("collab enabled"),
								enqueueActions(({ context, enqueue }) => {
									Object.values(context.pageActorRefs).forEach((pageActorRef) => {
										enqueue.sendTo(pageActorRef, {
											type: "document.collab.enabled",
										});
									});
								}),
							],
							target: "connecting",
						},
					},
				},
				connecting: {
					entry: () => debuglog("collab connecting"),
					invoke: {
						src: "connectToReboot",
						input: ({ context }) => {
							const workspaceName = workspaceNameFromDocumentListItem(
								context.listItem
							);
							return {
								workspaceName,
								documentId: context.listItem.id,
								metadata: context.metadata!,
								authenticationActorRef: context.authenticationActorRef,
							};
						},
						onDone: {
							actions: assign({ reboot: ({ event }) => event.output }),
							target: "listening",
						},
					},
				},
				listening: {
					entry: () => debuglog("collab listening"),
					invoke: {
						src: "listenForChanges",
						input: ({ context }) => ({
							workspaceName: workspaceNameFromDocumentListItem(context.listItem)!,
							documentId: context.listItem.id,
							reboot: context.reboot,
						}),
					},
					on: {
						"document.collab.connected": {
							actions: [assign({ store: ({ event }) => event.payload.store })],
						},
						// "document.metadata.remoteTransactionReceived": {
						// 	actions: "receiveRemoteTransaction",
						// },
						// "document.metadata.editorStateReconfigured": {
						// 	actions: assign(({ event }) => ({
						// 		editorState: event.payload.editorState,
						// 	})),
						// },
					},
				},
			},
		},
	},
});
